import { SEO } from "@/components/SEO";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock, Tag, ArrowLeft, ArrowRight, Phone, ChevronRight } from "lucide-react";
import { BUSINESS } from "@/data/businessInfo";
import { LeadCaptureForm } from "@/components/LeadCaptureForm";
import { ServiceCardsSection, LocationCardsSection } from "@/components/SharedSections";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/${slug}`).then(r => { setPost(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-[50vh] flex items-center justify-center"><p className="text-[#6B3A1F]">Loading...</p></div>;
  if (!post) return (
    <div className="min-h-[50vh] flex items-center justify-center flex-col gap-4">
      <h1 className="text-2xl font-bold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>Article Not Found</h1>
      <Link to="/blog" className="text-[#D97904] font-semibold">Back to Blog</Link>
    </div>
  );

  return (
    <div>
      <SEO title={`${post.title} | Southern Light Solutions Blog`} description={post.excerpt} path={`/blog/${slug}`} />

      {/* Breadcrumb */}
      <div className="bg-[#FAFAFA] border-b border-[#6B3A1F]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-2 text-xs text-[#6B3A1F]" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Link to="/" className="hover:text-[#D97904]">Home</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/blog" className="hover:text-[#D97904]">Blog</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[#401303] font-semibold truncate max-w-[200px]">{post.title}</span>
        </div>
      </div>

      <section className="bg-[#FAFAFA] py-10 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Article */}
            <article className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                {post.category && (
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#D97904]/10 rounded-full text-xs font-semibold text-[#6B3A1F]">
                    <Tag className="w-3 h-3" /> {post.category}
                  </span>
                )}
                {post.read_time && (
                  <span className="flex items-center gap-1 text-xs text-[#6B3A1F]">
                    <Clock className="w-3 h-3" /> {post.read_time} read
                  </span>
                )}
                <span className="text-xs text-[#6B3A1F]/60">{post.date}</span>
              </div>

              <h1 className="text-3xl sm:text-4xl font-bold text-[#401303] tracking-tight leading-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{post.title}</h1>

              <div className="flex items-center gap-3 mb-8 pb-6 border-b border-[#6B3A1F]/10">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#BF3604] to-[#D97904] flex items-center justify-center text-white text-sm font-bold">A</div>
                <div>
                  <p className="text-sm font-semibold text-[#401303]" style={{ fontFamily: 'Outfit, sans-serif' }}>Anthony Mazza</p>
                  <p className="text-xs text-[#6B3A1F]">Owner, Southern Light Solutions LLC</p>
                </div>
              </div>

              {/* Article Content */}
              <div className="prose max-w-none" data-testid="blog-post-content">
                {(post.content || post.excerpt).split('\n\n').map((para, i) => (
                  <p key={i} className="text-[#6B3A1F] leading-relaxed mb-5 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>{para.trim()}</p>
                ))}
              </div>

              {/* CTA after article */}
              <div className="mt-10 p-6 bg-gradient-to-r from-[#BF3604]/10 to-[#D97904]/10 rounded-2xl border border-[#D97904]/20">
                <h3 className="text-lg font-bold text-[#401303] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Need Help With This?</h3>
                <p className="text-sm text-[#6B3A1F] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Southern Light Solutions provides free estimates for all electrical services on the Treasure Coast. Call or schedule online today.</p>
                <div className="flex flex-wrap gap-3">
                  <Link to="/book" className="inline-flex items-center gap-2 px-6 py-3 btn-gradient rounded-full text-sm" data-testid="blog-post-cta">
                    Request a Free Estimate <ArrowRight className="w-4 h-4" />
                  </Link>
                  <a href={`tel:${BUSINESS.phoneRaw}`} className="inline-flex items-center gap-2 px-6 py-3 border-2 border-[#401303] text-[#401303] font-bold rounded-full text-sm hover:bg-[#401303] hover:text-white transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Phone className="w-4 h-4" /> {BUSINESS.phone}
                  </a>
                </div>
              </div>

              {/* Back to blog */}
              <div className="mt-8">
                <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-[#D97904] hover:text-[#BF3604] transition-colors duration-200" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <ArrowLeft className="w-4 h-4" /> Back to All Articles
                </Link>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-40 space-y-6">
                <LeadCaptureForm title="Get a Free Estimate" subtitle="Tell us about your electrical project." compact={true} />
              </div>
            </aside>
          </div>
        </div>
      </section>

      <ServiceCardsSection title="Our Electrical Services" />
      <LocationCardsSection title="Areas We Serve" />
    </div>
  );
}
